import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Logo from "../components/Logo";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [schools, setSchools] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        school_id: "",
        student_class: "",
        section: "",
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
    }, []);

    const selectedSchool = schools.find((s) => s.id === form.school_id);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.school_id) return setErr("Please select your school");
        if (selectedSchool && !form.email.toLowerCase().endsWith("@" + selectedSchool.email_domain.toLowerCase())) {
            return setErr(`Email must end with @${selectedSchool.email_domain}`);
        }
        setLoading(true);
        try {
            await register({ ...form, email: form.email.trim().toLowerCase() });
            navigate("/app/storefront");
        } catch (e) {
            setErr(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-lg">
                <Link to="/" className="flex items-center gap-2 mb-8 group" data-testid="register-brand">
                    <Logo height={96} />
                </Link>

                <div className="bg-white border border-parchment rounded-2xl p-8 shadow-auth">
                    <h1 className="font-serif text-3xl font-semibold mb-1">Join the exchange.</h1>
                    <p className="text-ink/60 mb-6">Get 50 Zapp coins on sign-up · first month of borrowing free.</p>

                    {schools.length === 0 && (
                        <div className="text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-md px-3 py-2 mb-4">
                            No schools registered yet. Please ask your school SPOC to onboard your school first.
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="label-mini">Full name</label>
                            <input data-testid="register-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-field" placeholder="Riya Sharma" />
                        </div>

                        <div>
                            <label className="label-mini">School</label>
                            <Select value={form.school_id} onValueChange={(v) => set("school_id", v)}>
                                <SelectTrigger data-testid="register-school-select" className="input-field h-auto py-3 justify-between">
                                    <SelectValue placeholder="Choose your school" />
                                </SelectTrigger>
                                <SelectContent>
                                    {schools.map((s) => (
                                        <SelectItem key={s.id} value={s.id} data-testid={`register-school-option-${s.id}`}>
                                            {s.name} — {s.branch} <span className="text-ink/40 ml-2">@{s.email_domain}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {selectedSchool && (
                            <div className="text-xs text-library bg-library/10 border border-library/20 rounded-md px-3 py-2">
                                Your email must end with <span className="font-bold font-mono">@{selectedSchool.email_domain}</span>
                            </div>
                        )}

                        <div>
                            <label className="label-mini">Email</label>
                            <input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="input-field" placeholder={selectedSchool ? `you@${selectedSchool.email_domain}` : "you@school.edu"} />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="label-mini">Class</label>
                                <input data-testid="register-class-input" required value={form.student_class} onChange={(e) => set("student_class", e.target.value)} className="input-field" placeholder="10" />
                            </div>
                            <div>
                                <label className="label-mini">Section</label>
                                <input data-testid="register-section-input" required value={form.section} onChange={(e) => set("section", e.target.value)} className="input-field" placeholder="A" />
                            </div>
                        </div>

                        <div>
                            <label className="label-mini">Password</label>
                            <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="input-field" placeholder="At least 6 characters" />
                        </div>

                        {err && <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

                        <button data-testid="register-submit-btn" type="submit" disabled={loading} className="btn-primary w-full">
                            {loading ? "Creating…" : "Create account"}
                        </button>
                    </form>

                    <div className="text-center text-sm text-ink/60 mt-6">
                        Already have an account?{" "}
                        <Link to="/login" className="text-brick font-semibold hover:underline" data-testid="register-goto-login">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
